// Quick view helpers. Nothing here runs until a shopper touches a quick view button.
//  1. the browser Back button closes the pop-up instead of leaving the listing page
//  2. the product is fetched on pointerdown (a finger is on the button ~100-300ms before the click
//     fires) and kept in memory, so opening is quick and reopening the same product is instant
//  3. quantity, Add to cart, Buy it now and View more are gathered into one bar that stays at the
//     bottom of the pop-up while the rest scrolls (the buttons stay inside their form, so adding to
//     the cart keeps working exactly as before)
//  4. the page behind the pop-up is held still, so swiping inside the sheet never scrolls the listing
(function () {
  let openModal = null; // the modal we pushed a history entry for
  let pushedState = false;
  const productCache = new Map(); // product url -> Promise<html string>
  const scrollTo = window.scrollTo; // kept aside: fc-product.js switches page scrolling off for a moment

  function fetchProduct(url) {
    if (!productCache.has(url)) {
      productCache.set(
        url,
        fetch(url)
          .then((response) => response.text())
          .catch(() => {
            productCache.delete(url); // a failed fetch must not be remembered
            return null;
          })
      );
    }
    return productCache.get(url);
  }

  // start loading as soon as the finger or cursor goes down, not on click
  document.addEventListener(
    'pointerdown',
    (event) => {
      const button = event.target.closest && event.target.closest('.quick-add__submit[data-product-url]');
      if (button) fetchProduct(button.getAttribute('data-product-url'));
    },
    { passive: true }
  );

  // ----- hold the page still behind the pop-up -----
  let lockedAt = 0;
  let locked = false;

  function lockPage() {
    if (locked) return;
    locked = true;
    lockedAt = window.scrollY || 0;
    // the theme pins body to position:relative, so this has to be stronger
    document.body.style.setProperty('top', -lockedAt + 'px', 'important');
    document.body.style.setProperty('width', '100%', 'important');
    document.body.style.setProperty('position', 'fixed', 'important');
  }

  function unlockPage() {
    if (!locked) return;
    locked = false;
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('width');
    scrollTo.call(window, 0, lockedAt);
  }

  // ----- the bar at the bottom of the pop-up -----
  function buildBar(modal) {
    const content = modal.querySelector('.quick-add-modal__content');
    const info = modal.querySelector('.product__info-container');
    if (!content || !info) return;
    const form = info.querySelector('product-form');
    if (!form) return;

    let bar = info.querySelector('.fcqv-bar');
    if (bar && form.parentElement === bar) return; // already built for this product
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'fcqv-bar';
    }
    info.appendChild(bar); // last thing in the column, so it can stick to the bottom

    bar.appendChild(form); // the whole form moves, never the buttons on their own

    // The pop-up empties itself when it closes, so the card's own View more link stays outside it and
    // a copy goes in the bar; that way the button is still there the second time it is opened.
    const source = content.querySelector('.fcqv__full:not([hidden])') || content.querySelector('.fcqv__full');
    if (source) {
      bar.querySelectorAll('.fcqv__full').forEach((old) => old.remove());
      const copy = source.cloneNode(true);
      copy.removeAttribute('hidden');
      source.setAttribute('hidden', '');
      bar.appendChild(copy);
    }

    placeQuantity(modal);
    fitBar(modal);
  }

  // The quantity box only fits in the bar on a wide screen. On a phone the bar holds just the two
  // buttons, so the quantity box stays in the page, right above the bar.
  function placeQuantity(modal) {
    const bar = modal.querySelector('.fcqv-bar');
    const info = modal.querySelector('.product__info-container');
    const quantity = info && info.querySelector('.fcq');
    if (!bar || !quantity) return;
    const wide = window.innerWidth >= 750;
    if (wide && quantity.parentElement !== bar) bar.insertBefore(quantity, bar.firstChild);
    if (!wide && quantity.parentElement === bar) info.insertBefore(quantity, bar);
  }

  // On a wide screen the bar reaches across the photo as well, right out to both edges of the card.
  // Measure the gap that is left on each side and pull the bar out by that much; a second pass
  // settles the last pixel or two (scrollbar, rounding).
  function fitBar(modal) {
    const content = modal.querySelector('.quick-add-modal__content');
    const bar = modal.querySelector('.fcqv-bar');
    if (!content || !bar) return;
    if (window.innerWidth < 750) {
      bar.style.removeProperty('--fcqv-left');
      bar.style.removeProperty('--fcqv-right');
      return;
    }
    const value = (name) => parseFloat(bar.style.getPropertyValue(name)) || 0;
    for (let pass = 0; pass < 2; pass++) {
      const card = content.getBoundingClientRect();
      const here = bar.getBoundingClientRect();
      bar.style.setProperty('--fcqv-left', Math.max(0, Math.round(value('--fcqv-left') + here.left - card.left)) + 'px');
      bar.style.setProperty('--fcqv-right', Math.max(0, Math.round(value('--fcqv-right') + card.right - here.right)) + 'px');
    }
  }

  let resizeTimer;
  window.addEventListener('resize', function () {
    if (!openModal || !openModal.hasAttribute('open')) return; // nothing to do unless one is open
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      placeQuantity(openModal);
      fitBar(openModal);
    }, 150);
  });

  // the pop-up rebuilds its contents when a size is chosen, so put the bar back together afterwards
  function watch(modal) {
    if (modal.fcWatched) return;
    modal.fcWatched = true;
    let timer;
    new MutationObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => buildBar(modal), 40);
    }).observe(modal, { childList: true, subtree: true });

    // Closed with the X, the overlay, Esc or Back. The theme wires its own close button up before we
    // get a look in, so watch the pop-up itself rather than its hide() - that way nothing is missed.
    new MutationObserver(() => {
      if (modal.hasAttribute('open')) return;
      unlockPage(); // the listing goes back to exactly where it was
      if (openModal === modal) openModal = null;
      if (pushedState) {
        pushedState = false;
        history.back(); // drop the entry we added, without leaving the page
      }
    }).observe(modal, { attributes: true, attributeFilter: ['open'] });
  }

  window.addEventListener('popstate', function () {
    // Back pressed while the pop-up is open: close it and stay on this page and scroll position
    const modal = openModal;
    openModal = null;
    pushedState = false;
    if (modal && modal.hasAttribute('open')) modal.hide();
  });

  customElements.whenDefined('quick-add-modal').then(function () {
    const QuickAddModal = customElements.get('quick-add-modal');
    if (!QuickAddModal || QuickAddModal.prototype.fcQuickViewPatched) return;
    const originalShow = QuickAddModal.prototype.show;

    // same rendering steps as quick-add.js, but fed from the cache instead of a fresh fetch
    QuickAddModal.prototype.fcRender = function (html, opener) {
      const responseHTML = new DOMParser().parseFromString(html, 'text/html');
      const productElement = responseHTML.querySelector('product-info');
      if (!productElement) return false;

      this.preprocessHTML(productElement);
      HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

      if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
      if (window.ProductModel) window.ProductModel.loadShopifyXR();
      this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

      Object.getPrototypeOf(QuickAddModal.prototype).show.call(this, opener);
      return true;
    };

    QuickAddModal.prototype.show = function (opener) {
      openModal = this;
      watch(this);
      if (!pushedState) {
        // same URL, so closing never navigates away; Back just pops this entry
        history.pushState({ fcQuickView: true }, '', window.location.href);
        pushedState = true;
      }

      const opened = () => {
        if (!this.hasAttribute('open')) return; // product gone or fetch failed: nothing opened
        buildBar(this);
        lockPage();
      };

      const url = opener.getAttribute('data-product-url');
      if (!url) return Promise.resolve(originalShow.call(this, opener)).then(opened);

      const spinner = opener.querySelector('.loading__spinner');
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');

      return fetchProduct(url)
        .then((html) => {
          if (!html || !this.fcRender(html, opener)) return originalShow.call(this, opener);
        })
        .then(opened)
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        });
    };

    QuickAddModal.prototype.fcQuickViewPatched = true;
  });
})();
